import React, { useContext, useRef, useState } from 'react';

import { NewInventoryFiltersContext } from '@/contexts/NewInventoryFiltersContext';
import { useCustomSearchParams } from '@/hooks/useCustomSearchParams';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetInventoryItems } from '@lamesarv-sdk/inventory';
import { escapeRegexCharacters, getInventoryPageUrl } from '@lamesarv-sdk/tools';
import { InventoryField, PropsWithClasses } from '@lamesarv-sdk/types';
import { AutoCompleteBase, ISuggestion, ISuggestions } from './AutoCompleteBase';

export interface IAutoCompleteProps extends PropsWithClasses {
  placeholder?: string;
  searchPath?: string;
  sort?: string;
  amount?: number;
  onSuggestionsFetch?: (value: string) => Promise<ISuggestions[]>;
  customSuggestionMap?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [type: string]: React.FC<any>;
  };
  debounceMilliseconds?: number;
  onEnter?: (value: string, suggestions: ISuggestions[]) => void;
  defaultSearch?: string;
  onChange?: (value: string) => void;
  onClick?: (value: string) => void;
  overrideOnEnter?: boolean;
}

export const SdkAutoComplete = (props: IAutoCompleteProps) => {
  const { error, fetchItems } = useGetInventoryItems({
    skipFetch: true,
  });
  const { searchParams } = useCustomSearchParams();
  const { setQuery } = useContext(NewInventoryFiltersContext);
  const [isLoading, setIsLoading] = useState(false);
  const lastValueRef = useRef<string>();

  const onSuggestionsFetch = async (value: string) => {
    setIsLoading(true);

    try {
      const suggestions: ISuggestions[] = [];

      const [inventoryResults, customSuggestions] = await Promise.all([
        fetchItems({
          query: escapeRegexCharacters(value),
          sort: props.sort,
          currentPage: 0,
          perPage: props.amount ?? 5,
        }),
        props.onSuggestionsFetch?.(value),
      ]);

      const inventorySuggestions = inventoryResults.hits.map((hit) => {
        const item = hit.document;
        return {
          ...item,
          _type: 'inventory',
          _code: item[InventoryField.stockNumber],
          _title: item[InventoryField.title],
          _description: `${item[InventoryField.body]} - ${item[InventoryField.make]} - ${item[InventoryField.model]}`,
          _url: getInventoryPageUrl(item),
        };
      });

      suggestions.push({
        sectionTitle: 'Inventory Items',
        suggestions: inventorySuggestions,
      });

      if (customSuggestions) suggestions.push(...customSuggestions);

      return suggestions;
    } finally {
      setIsLoading(false);
    }
  };

  const navigateTo = (url: string) => {
    window.location.href = url;
  };

  const onSuggestionSelected = async (suggestion: ISuggestion) => {
    if (props.onClick && lastValueRef.current) props.onClick(lastValueRef.current);
    navigateTo(suggestion._url ?? '#');
  };

  const [onChange] = useDebounce(async (value: string) => {
    setQuery(value || '');
  }, props.debounceMilliseconds ?? 1000);

  const onEnter = (value: string, suggestions: ISuggestions[]) => {
    const searchParamsObj = new URLSearchParams({ ...searchParams.entries, q: value });
    const searchPath = props.searchPath ?? '/search';

    if (props.onEnter) props.onEnter(value, suggestions);

    if (suggestions.length > 0) {
      const [inventorySuggestionGroup] = suggestions;

      const suggestion =
        inventorySuggestionGroup.suggestions.length === 1
          ? inventorySuggestionGroup.suggestions[0]
          : inventorySuggestionGroup.suggestions.find(
              (suggestion) => suggestion._code.toLowerCase() === value.toLocaleLowerCase(),
            );

      if (suggestion) {
        navigateTo(suggestion._url ?? '#');
        return;
      }
    }

    navigateTo(`${searchPath}${searchParamsObj && '?'}${searchParamsObj?.toString()}`);
  };

  let defaultSearch: string | undefined = props.defaultSearch;
  if (!defaultSearch) {
    defaultSearch = searchParams.get('q') || '';
  }

  return (
    <AutoCompleteBase
      placeholder={props.placeholder ?? 'Search Inventory'}
      onChange={(value) => {
        // This is a hack to get the value of the input field before the onChange event is triggered
        // So we don't get the unit stock number as the value
        setTimeout(() => {
          lastValueRef.current = value;
        }, 0);

        if (props.onChange) props.onChange(value);

        return onChange(value);
      }}
      onEnter={(props.overrideOnEnter && props.onEnter) || onEnter}
      onSuggestionsFetch={onSuggestionsFetch}
      onSuggestionSelected={onSuggestionSelected}
      isLoading={isLoading}
      isError={error}
      className={props.className}
      classIcon={props.classIcon}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      customSuggestionMap={props.customSuggestionMap as any}
      debounceMilliseconds={props.debounceMilliseconds}
      defaultSearch={defaultSearch}
    />
  );
};
